package top.wcpe.allincore.ui.web.model

internal data class BbResolution(val width: Double, val height: Double)

internal data class BbVector3(val x: Double, val y: Double, val z: Double)

internal data class BbUvRect(val u1: Double, val v1: Double, val u2: Double, val v2: Double)

internal enum class BbFaceDirection {
    NORTH,
    EAST,
    SOUTH,
    WEST,
    UP,
    DOWN,
}

internal data class RestrictedBbFace(
    val direction: BbFaceDirection,
    val uv: BbUvRect,
    val textureIndex: Int,
)

internal data class RestrictedBbCube(
    val uuid: String,
    val name: String?,
    val from: BbVector3,
    val to: BbVector3,
    val origin: BbVector3,
    val rotation: BbVector3,
    val faces: List<RestrictedBbFace>,
)

internal sealed interface RestrictedBbOutlinerNode

internal data class RestrictedBbGroupNode(
    val uuid: String,
    val name: String?,
    val origin: BbVector3,
    val rotation: BbVector3,
    val children: List<RestrictedBbOutlinerNode>,
) : RestrictedBbOutlinerNode

internal data class RestrictedBbCubeNode(val cubeUuid: String) : RestrictedBbOutlinerNode

internal enum class RestrictedBbRelativeBase {
    FILE,
    DIRECTORY,
}

internal data class RestrictedBbRelativeTextureRequest(
    val referrerCanonicalPath: String,
    val relativePath: String,
    val canonicalPath: String,
    val base: RestrictedBbRelativeBase,
)

internal sealed interface RestrictedBbTextureSource

internal data class RestrictedBbExternalTexture(
    val request: RestrictedBbRelativeTextureRequest,
) : RestrictedBbTextureSource

internal class RestrictedBbInlineTexture(
    val mime: String,
    bytes: ByteArray,
) : RestrictedBbTextureSource {
    private val value = bytes.copyOf()

    val bytes: ByteArray
        get() = value.copyOf()

    override fun equals(other: Any?): Boolean =
        other is RestrictedBbInlineTexture && mime == other.mime && value.contentEquals(other.value)

    override fun hashCode(): Int = 31 * mime.hashCode() + value.contentHashCode()

    override fun toString(): String = "RestrictedBbInlineTexture(mime=$mime, bytes=${value.size})"
}

internal enum class RestrictedBbTextureSourceKind {
    DSL_OVERRIDE,
    RELATIVE_PATH,
    INLINE_DATA,
}

internal data class RestrictedBbTexture(
    val index: Int,
    val name: String?,
    val sourceKind: RestrictedBbTextureSourceKind,
    val source: RestrictedBbTextureSource,
)

internal data class RestrictedBbTextureOverride(
    val canonicalBaseDirectory: String,
    val relativePath: String,
)

internal data class RestrictedBbStructureBudget(
    val cubeCount: Int,
    val outlinerNodeCount: Int,
    val faceCount: Int,
    val vertexCount: Int,
)

internal data class RestrictedBbGpuBudget(
    val geometryObjects: Int,
    val materialObjects: Int,
    val textureObjects: Int,
)

internal data class RestrictedBbWarning(
    val code: String,
    val location: String,
    val message: String,
)

internal data class RestrictedBbModel(
    val resolution: BbResolution,
    val cubes: List<RestrictedBbCube>,
    val roots: List<RestrictedBbOutlinerNode>,
    val textures: List<RestrictedBbTexture>,
    val structureBudget: RestrictedBbStructureBudget,
    val gpuBudget: RestrictedBbGpuBudget,
    val warnings: List<RestrictedBbWarning>,
)

internal class RestrictedBbModelException(
    val code: String,
    val location: String,
    detail: String,
) : IllegalArgumentException("$code：$detail（位置：$location）")

internal object RestrictedBbModelLimits {
    const val JSON_DEPTH = 64
    const val CUBES = 2048
    const val OUTLINER_NODES = 4096
    const val OUTLINER_DEPTH = 128
    const val FACES = 12288
    const val VERTICES = 49152
    const val GEOMETRIES = 2048
    const val MATERIALS = 1536
    const val TEXTURES = 256
}
